// explore.jsx — the full-screen Explore view: the source map as a destination.
// Floating chrome over a pan/zoom canvas: mode switcher, type filters,
// traced paths, era timeline, and a rich detail panel for any node.
const { useState: useStateE, useMemo: useMemoE, useEffect: useEffectE, useRef: useRefE } = React;

const normalizeExploreRef = (value) => String(value || "").replace(/_/g, " ").replace(/\s+/g, " ").trim().toLowerCase();

function exploreScopeContext() {
  const text = window.SICHA || {};
  const origin = window.MAYANOS_EXPLORE_ORIGIN || window.__mayanosReaderLocation || {};
  const currentRef = origin.sectionRef || text._liveTargetRef || text._liveRef || text.id || "";
  const loaded = (text._loadedSections || []).find((section) =>
    [section.ref, section.canonicalRef, section.targetRef].some((ref) => normalizeExploreRef(ref) === normalizeExploreRef(currentRef))
  ) || (text._loadedSections || [])[0] || {};
  const aliases = [...new Set([currentRef, loaded.ref, loaded.canonicalRef, loaded.targetRef, text._liveRef, text._liveTargetRef].filter(Boolean))];
  const toc = window.__mayanosTOC;
  const sections = toc && Array.isArray(toc.sections) ? toc.sections : [];
  const currentIndex = sections.findIndex((section) => section && !section.isHeader &&
    [section.ref, section.readerRef, section.targetRef].some((ref) => aliases.some((alias) => normalizeExploreRef(ref) === normalizeExploreRef(alias)))
  );
  const currentEntry = currentIndex >= 0 ? sections[currentIndex] : null;
  let workEntries = currentEntry ? sections.filter((section) => {
    if (!section || section.isHeader) return false;
    if (Array.isArray(currentEntry.path) && currentEntry.path.length) {
      return (section.path || []).join("|") === currentEntry.path.join("|");
    }
    return section === currentEntry;
  }) : [];
  let header = null;
  if (currentIndex >= 0) {
    for (let i = currentIndex - 1; i >= 0; i--) {
      if (sections[i] && sections[i].isHeader) { header = sections[i]; break; }
    }
  }
  if (!workEntries.length && currentEntry) workEntries = [currentEntry];
  const targetHay = `${currentRef} ${loaded.targetRef || ""} ${text.collection || ""}`;
  const unit = /:maamar:|\bmaamar\b/i.test(targetHay) ? "maamar"
    : /:sicha:|\bsicha\b|likkutei sichos/i.test(targetHay) ? "sicha"
      : /tanya|likkutei amarim/i.test(targetHay) ? "perek"
        : /:letter:|\bletter\b/i.test(targetHay) ? "letter" : "text";
  const workLabel = (currentEntry && currentEntry.path && currentEntry.path.at(-1)) || (header && header.label) || text.titleEn || currentRef;
  const workHe = (header && header.heLabel) || text.titleHe || "";
  return {
    origin,
    currentRef,
    publicRef: (currentEntry && currentEntry.ref) || loaded.canonicalRef || loaded.ref || currentRef,
    aliases,
    currentEntry,
    workEntries,
    workRefs: workEntries.map((entry) => entry.readerRef || entry.targetRef || entry.ref).filter(Boolean),
    unit,
    workLabel,
    workHe,
  };
}

function sourceInExploreSection(source, aliases) {
  const anchors = [...(source.anchorRefs || []), source.anchorRef].filter(Boolean).map(normalizeExploreRef);
  return anchors.some((anchor) => aliases.some((alias) => {
    const scope = normalizeExploreRef(alias);
    return anchor === scope || anchor.startsWith(scope + ":");
  }));
}

function ExplorePage({ t, setTweak, onOpenReader, onBackReader }) {
  const isMobile = window.useIsMobile();
  // The live text arrives asynchronously, including on a direct /explore visit.
  const scopeContext = exploreScopeContext();
  const savedState = window.__mayanosExploreState || {};
  const defaultScope = "work";
  const requestedScope = defaultScope;
  const [scope, setScope] = useStateE(requestedScope);
  const [viewMode, setViewMode] = useStateE(savedState.viewMode || "radial");
  const sourceSignature = (window.QUOTED_SOURCES || []).map((source) => `${source.id}|${source.srcRef || source.citation}|${source.anchorRef || ""}`).join(";");
  const sectionSources = useMemoE(() => (window.QUOTED_SOURCES || []).filter((source) => sourceInExploreSection(source, scopeContext.aliases)), [sourceSignature, scopeContext.aliases.join("|")]);
  const sectionMap = useMemoE(() => window.createSourceMap({
    sources: sectionSources,
    center: { titleEn: window.SICHA && window.SICHA.collection, _liveRef: scopeContext.publicRef },
  }), [sectionSources]);
  const restoredMap = savedState.scope === scope && savedState.map && Array.isArray(savedState.map.nodes) ? savedState.map : null;
  const [map, setMap] = useStateE(restoredMap || sectionMap);
  const [loadingScope, setLoadingScope] = useStateE(false);
  const [scopeError, setScopeError] = useStateE("");
  const [retryScope, setRetryScope] = useStateE(0);
  const [workSectionCount, setWorkSectionCount] = useStateE(0);
  const [scopeMapReady, setScopeMapReady] = useStateE(scope !== "work");
  const MAP = map;
  const [selected, setSelected] = useStateE(savedState.selected || null);
  const [chainId, setChainId] = useStateE(null);
  const [hiddenTypes, setHiddenTypes] = useStateE([]);
  const [era, setEra] = useStateE(null);
  const [tools, setTools] = useStateE(false);
  const [sourceQuery, setSourceQuery] = useStateE("");
  const [nodePreview, setNodePreview] = useStateE(null);
  const [discoveryIndex, setDiscoveryIndex] = useStateE(0);
  const ws = window.useWorkspace();
  const launchHandled = useRefE(false);

  useEffectE(() => {
    window.__mayanosExploreState = Object.assign({}, window.__mayanosExploreState || {}, { scope, selected, map, viewMode });
  }, [scope, selected, map, viewMode]);

  const previousScope = useRefE(scope);
  useEffectE(() => {
    if (previousScope.current !== scope) {
      setSelected(null);
      setHiddenTypes([]);
      setEra(null);
    }
    previousScope.current = scope;
  }, [scope]);

  useEffectE(() => {
    if (scope === "section") {
      setMap(sectionMap);
      setLoadingScope(false);
      setScopeMapReady(true);
    }
  }, [scope, sectionMap]);

  const workRefsKey = scopeContext.workRefs.join("|");
  useEffectE(() => {
    let live = true;
    if (scope !== "work" || !window.loadWholeSichaSources) return () => { live = false; };
    if (!scopeContext.currentRef) { setLoadingScope(true); return () => { live = false; }; }
    setLoadingScope(true);
    setScopeError("");
    setScopeMapReady(false);
    window.loadWholeSichaSources(scopeContext.currentRef).then(({ sources, refs, label, he }) => {
      if (!live) return;
      setWorkSectionCount(refs.length);
      setMap(window.createSourceMap({ sources, center: { titleEn: window.SICHA?.collection || label, titleHe: he, _liveRef: label } }));
      setScopeMapReady(true);
    }).catch((error) => { if (live) setScopeError(error.message || "Could not load all connections."); })
      .finally(() => { if (live) setLoadingScope(false); });
    return () => { live = false; };
  }, [scope, scopeContext.currentRef, workRefsKey, retryScope]);

  const node = selected ? MAP.nodes.find((n) => n.id === selected) : null;
  const center = MAP.nodes.find((n) => n.id === "center") || {};
  const chain = chainId ? MAP.chains.find((c) => c.id === chainId) : null;
  const eraOf = (n) => MAP.eras.find((e) => e.id === n.era);
  const pool = useMemoE(() => MAP.nodes.filter((n) => n.id !== "center"), [MAP]);
  const types = useMemoE(() => {
    const m = {};
    pool.forEach((n) => { m[n.type] = (m[n.type] || 0) + 1; });
    return Object.entries(m);
  }, [pool]);
  const listedSources = pool.filter(item => !hiddenTypes.includes(item.type) && (!era || item.era === era) && `${item.title} ${item.citation} ${item.type} ${item.he || ""}`.toLowerCase().includes(sourceQuery.toLowerCase()));
  const dominantType = types.slice().sort((a, b) => b[1] - a[1])[0] || ["Sources", 0];
  const repeatedSources = pool.filter((item) => Number(item.anchorCount || (item.anchorRefs || []).length || 1) > 1);
  const browseGroups = useMemoE(() => MAP.eras.map((group) => ({
    ...group,
    nodes: pool.filter((item) => item.era === group.id && !hiddenTypes.includes(item.type) && (!era || item.era === era)),
  })).filter((group) => group.nodes.length), [MAP, pool, hiddenTypes.join("|"), era]);
  const eraCounts = useMemoE(() => {
    const m = {};
    pool.forEach((n) => { m[n.era] = (m[n.era] || 0) + 1; });
    return m;
  }, [pool]);
  const nodeChains = node ? MAP.chains.filter((c) => c.nodes.includes(node.id)) : [];
  const collected = node && ws.collected.map(String).includes(window.WS.sourceKey(node));

  useEffectE(() => {
    let live = true;
    setNodePreview(null);
    if (!node || node.id === "center" || !(node.srcRef || node.citation)) return () => { live = false; };
    const ref = node.srcRef || node.citation;
    fetch(`/bff/text/${encodeURIComponent(String(ref).replace(/ /g, "_"))}`)
      .then((response) => response.ok ? response.json() : null)
      .then((text) => {
        if (!live || !text || !Array.isArray(text.segments)) return;
        const wanted = normalizeExploreRef(text.focusRef || ref);
        const exact = text.segments.find((segment) => normalizeExploreRef(segment.ref) === wanted);
        const segments = exact ? [exact] : text.segments.slice(0, 2);
        setNodePreview({
          he: segments.map((segment) => String(segment.he || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim()).filter(Boolean).join(" "),
          en: segments.map((segment) => String(segment.en || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim()).filter(Boolean).join(" "),
        });
      }).catch(() => {});
    return () => { live = false; };
  }, [node && node.id]);

  const toggleType = (ty) =>
    setHiddenTypes((p) => (p.includes(ty) ? p.filter((x) => x !== ty) : [...p, ty]));

  const surpriseMe = () => {
    if (!pool.length) return;
    const next = (discoveryIndex + 1) % pool.length;
    setDiscoveryIndex(next);
    setSelected(pool[next].id);
  };

  useEffectE(() => {
    if (launchHandled.current || !savedState.surprise || !scopeMapReady || !pool.length) return;
    launchHandled.current = true;
    const index = Math.floor(Math.random() * pool.length);
    setDiscoveryIndex(index);
    setSelected(pool[index].id);
    window.__mayanosExploreState = Object.assign({}, window.__mayanosExploreState || {}, { surprise: false, selected: pool[index].id });
  }, [pool.length, savedState.surprise, scopeMapReady]);

  const showAnchorInReader = (source) => {
    const anchorRef = source && source.anchorRef;
    if (!anchorRef) return;
    const sectionRef = String(anchorRef).replace(/:\d+$/, "");
    const loaded = ((window.SICHA && window.SICHA._loadedSections) || []).some((section) =>
      [section.ref, section.targetRef, section.canonicalRef].some((ref) => normalizeExploreRef(ref) === normalizeExploreRef(sectionRef))
    );
    const reveal = () => {
      onOpenReader();
      setTimeout(() => window.dispatchEvent(new CustomEvent("mayanos-scroll-segment", { detail: { ref: anchorRef, source } })), 140);
    };
    if (loaded || !window.loadSicha) { reveal(); return; }
    const origin = { url: "/explore", route: "explore", title: "the source map", exploreState: { scope, selected: source.id, viewMode } };
    window.__mayanosExploreState = Object.assign({}, window.__mayanosExploreState || {}, origin.exploreState, { map });
    window.loadSicha(sectionRef, { origin, recordActivity: false }).then((didLoad) => { if (didLoad) reveal(); });
  };

  return (
    <div className={"explore map-workbench" + (node ? " has-selection" : "")} data-screen-label="Explore — source map">
      {viewMode !== "browse" && <SourceMapPro mode={viewMode} edgeStyle="line" labels="hover"
        hiddenTypes={hiddenTypes} era={era} selected={selected} onSelect={setSelected} activeChain={chainId}
        onEraClick={(id) => setEra(era === id ? null : id)} map={MAP} />}

      {viewMode === "browse" && (
        <main className="exp-browse" aria-label="Browse sources by era">
          {browseGroups.map((group) => (
            <section className="exp-browse-group" key={group.id}>
              <header><span lang="he" dir="rtl" className="heb">{group.he}</span><b>{group.label}</b><i>{group.nodes.length}</i></header>
              <div className="exp-browse-grid">
                {group.nodes.map((item) => {
                  const excerpt = item.contextEn || (item.quoteEn || [])[0] || item.contextHe || (item.quoteHe || [])[0] || "";
                  const englishExcerpt = Boolean(item.contextEn || (item.quoteEn || [])[0]);
                  const occurrences = Number(item.anchorCount || (item.anchorRefs || []).length || 1);
                  return <button key={item.id} className={"exp-browse-card" + (selected === item.id ? " on" : "")} onClick={() => setSelected(item.id)}>
                    <span className="exp-browse-type"><TypeDot type={item.type} size={7} /> {item.type}</span>
                    <strong>{item.title}</strong>
                    <small>{item.citation}</small>
                    {excerpt && <p className={englishExcerpt ? "serif" : "heb"}>{excerpt}</p>}
                    <em>{occurrences} occurrence{occurrences === 1 ? "" : "s"}</em>
                  </button>;
                })}
              </div>
            </section>
          ))}
        </main>
      )}

      {scopeError && <div className="exp-loading" role="alert">{scopeError} <button onClick={() => setRetryScope(n => n + 1)}>Retry</button> <button onClick={() => setScope("section")}>View this section</button></div>}
      {loadingScope && <div className="exp-loading" role="status"><span className="account-spinner" /> Mapping the whole {scopeContext.unit}…</div>}

      {/* head */}
      <div className="exp-head">
        <div className="exp-head-row">
          <button className="exp-back" onClick={onBackReader}><Icon d="back" size={14} /> Back to reader</button>
          <div className="exp-eyebrow">Sources & connections</div>
        </div>
        <h1 className="exp-title">Source map <span lang="he" dir="rtl" className="heb">{center.he || "מפת מקורות"}</span></h1>
        <div className="exp-sub">{center.title || "Current text"} · {center.citation || ""} — {loadingScope ? "Loading all connections…" : scopeError ? "Full-text map unavailable" : `${pool.length} sources${scope === "work" ? ` across ${workSectionCount} sections` : ""}`}</div>
        <div className="exp-modes">
          <div className="exp-scope" role="tablist" aria-label="Explore scope">
            <button role="tab" aria-selected={scope === "section"} className={scope === "section" ? "on" : ""} onClick={() => setScope("section")}>This section</button>
            <button role="tab" aria-selected={scope === "work"} className={scope === "work" ? "on" : ""} onClick={() => setScope("work")}>Whole {scopeContext.unit}</button>
          </div>
          <div className="dirswitch">
            {[["radial", "Connections", "graph"], ["river", "By era", "path"], ["browse", "Source list", "layers"]].map(([k, label, ic]) => (
              <button key={k} className={"dir-btn" + (viewMode === k ? " on" : "")} onClick={() => { setViewMode(k); if (k !== "browse") setTweak("mapMode", k); }}>
                <Icon d={ic} size={14} /> {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* left rail (desktop) / bottom-sheet (mobile): filters + traced paths */}
      {isMobile && tools && <div className="exp-tools-scrim" onClick={() => setTools(false)} />}
      {(!isMobile || tools) && (
        <div className={"exp-side" + (isMobile ? " exp-side-sheet" : "")}>
          {isMobile && <div className="exp-tools-grip"><span></span></div>}
          <div className="map-index-head">
            <div className="exp-h">Sources <span>{listedSources.length}</span></div>
            {isMobile && <button className="map-index-close" onClick={() => setTools(false)} aria-label="Close source list"><Icon d="close" size={16}/></button>}
            <label className="map-index-search"><Icon d="search" size={15}/><input aria-label="Find a mapped source" placeholder="Find a source…" value={sourceQuery} onChange={e => setSourceQuery(e.target.value)}/></label>
            <SourceEraLegend map={MAP} selected={era} onSelect={setEra}/><details className="map-filter"><summary>Filter by source type</summary>{types.map(([ty,count]) => <button key={ty} className={"exp-type" + (hiddenTypes.includes(ty) ? " off" : "")} aria-pressed={!hiddenTypes.includes(ty)} onClick={() => toggleType(ty)}><TypeDot type={ty} size={7}/>{ty}<i>{count}</i></button>)}</details>
            {(hiddenTypes.length > 0 || era) && <button className="link-btn" onClick={() => {setHiddenTypes([]);setEra(null);}}>Clear filters</button>}
          </div>
          <div className="map-source-index">
            {listedSources.map(item => <button key={item.id} className={"map-source-row" + (selected === item.id ? " on" : "")} aria-pressed={selected === item.id} onClick={() => {setSelected(item.id); if(isMobile) setTools(false);}}>
              <span className="map-source-number" style={{color:window.sourceEraColor(item.era),borderColor:window.sourceEraColor(item.era)}}>{item.n}</span><span><b>{item.title}</b><small>{item.citation}</small><em>{item.type}{Number(item.anchorCount || item.anchorRefs?.length || 1) > 1 ? ` · ${item.anchorCount || item.anchorRefs.length} occurrences` : ""}</em></span><Icon d="chev" size={12}/>
            </button>)}
            {!listedSources.length && <p className="map-index-empty">{loadingScope ? "Loading sources…" : "No matching sources. Try another title or clear your filters."}</p>}
          </div>
          {MAP.chains.length > 0 && <div className="exp-card">
            <div className="exp-h">Traced paths</div>
            {MAP.chains.map((c) => (
              <button key={c.id} className={"exp-chain" + (chainId === c.id ? " on" : "")}
                style={{ "--cc": c.color }} onClick={() => { setChainId(chainId === c.id ? null : c.id); if (isMobile) setTools(false); }}>
                <span className="exp-chain-swatch"></span>
                <span>{c.title}<i>{c.nodes.length - 1} steps</i></span>
              </button>
            ))}
            {chain && (
              <p className="exp-chain-blurb">{chain.blurb}
                <button className="link-btn" style={{ marginLeft: 6 }} onClick={() => setChainId(null)}>Clear</button>
              </p>
            )}
          </div>}
          {isMobile && (
            <div className="exp-card">
              <div className="exp-h">By era</div>
              <div className="exp-eras-grid">
                {MAP.eras.map((e) => (
                  <button key={e.id} className={"exp-era" + (era === e.id ? " on" : "")}
                    onClick={() => { setEra(era === e.id ? null : e.id); }}>
                    <span lang="he" dir="rtl" className="heb">{e.he}</span>
                    <b>{e.label}</b>
                    <i>{eraCounts[e.id] || 0}</i>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* detail panel */}
      {node && (
        <aside className="exp-detail">
          {!node ? (
            <div className="exp-card exp-empty">
              <Icon d="compass" size={22} style={{ color: "var(--accent)" }} />
              <div className="exp-empty-t">{pool.length} sources mapped for this text.</div>
              <div className="exp-empty-s">Click any node to inspect the citation, filter by source type, or trace the available paths through the live source graph.</div>
            </div>
          ) : (
            <div className="exp-card exp-node" key={node.id}>
              <div className="exp-node-top">
                <span className="chip"><TypeDot type={node.type} size={7} /> {node.type}</span>
                {eraOf(node) && <span className="chip">{eraOf(node).label}</span>}
                <button className="exp-x" onClick={() => setSelected(null)} aria-label="Close"><Icon d="close" size={15} /></button>
              </div>
              <div className="exp-node-title">{node.title} <span>{node.citation}</span></div>
              <div className="exp-node-rel">{node.relationship}</div>
              {(nodePreview && nodePreview.he || node.he) && <p lang="he" dir="rtl" className="heb exp-node-he">{nodePreview && nodePreview.he || node.he}</p>}
              {(nodePreview && nodePreview.en || node.en) && <p className="exp-node-en serif">{nodePreview && nodePreview.en || node.en}</p>}
              {node.id !== "center" && ((node.contextHe || (node.quoteHe || [])[0]) || (node.contextEn || (node.quoteEn || [])[0])) && (
                <div className="exp-quote-context">
                  <b><Icon d="quote" size={13} /> Where it appears</b>
                  {(node.contextHe || (node.quoteHe || [])[0]) && <p lang="he" dir="rtl" className="heb">{node.contextHe || (node.quoteHe || [])[0]}</p>}
                  {(node.contextEn || (node.quoteEn || [])[0]) && <p className="serif">{node.contextEn || (node.quoteEn || [])[0]}</p>}
                  {node.anchorRef && <button className="mini-btn" onClick={() => showAnchorInReader(node)}><Icon d="back" size={12} /> Show in reader</button>}
                </div>
              )}
              {node.id !== "center" && (
                <div className="exp-node-inf"><span>{Number(node.anchorCount || (node.anchorRefs || []).length || 1)} occurrence{Number(node.anchorCount || (node.anchorRefs || []).length || 1) === 1 ? "" : "s"} in this {scope === "work" ? scopeContext.unit : "section"}</span></div>
              )}
              {nodeChains.length > 0 && (
                <div className="exp-node-chains">
                  {nodeChains.map((c) => (
                    <button key={c.id} style={{ "--cc": c.color }} className={"exp-chain" + (chainId === c.id ? " on" : "")}
                      onClick={() => setChainId(chainId === c.id ? null : c.id)}>
                      <span className="exp-chain-swatch"></span>
                      <span>{c.title}<i>Trace this path</i></span>
                    </button>
                  ))}
                </div>
              )}
              <div className="exp-node-actions">
                <button className="btn btn-primary" onClick={() => {
                  const ref = node.srcRef || node.citation;
                  const origin = { url: "/explore", route: "explore", title: "the source map", exploreState: { scope, selected: node.id, viewMode } };
                  window.__mayanosExploreState = Object.assign({}, window.__mayanosExploreState || {}, origin.exploreState, { map });
                  if (node.id !== "center" && ref && window.loadSicha) window.loadSicha(ref, { origin }).then((loaded) => { if (loaded) onOpenReader(); });
                  else onOpenReader();
                }}><Icon d="book" size={15} /> Open in reader</button>
                {node.id !== "center" && node.he && (
                  <button className="btn" onClick={() => window.openComparePair && window.openComparePair(node.id)}><Icon d="compare" size={15} /> Compare</button>
                )}
                {node.n != null && (collected
                  ? <button className="btn" disabled style={{ opacity: 0.7 }}><Icon d="check" size={15} /> In workspace</button>
                  : <button className="btn" onClick={() => window.WS.collect(node)}><Icon d="plus" size={15} /> Save source</button>)}
              </div>
            </div>
          )}
        </aside>
      )}

      {/* era timeline (in river mode the column footers take over) */}
      {false && viewMode === "radial" && (
      <div className="exp-timeline">
        {MAP.eras.map((e) => (
          <button key={e.id} className={"exp-era" + (era === e.id ? " on" : "")}
            onClick={() => setEra(era === e.id ? null : e.id)} title={e.sub}>
            <span lang="he" dir="rtl" className="heb">{e.he}</span>
            <b>{e.label}</b>
            <i>{eraCounts[e.id] || 0}</i>
          </button>
        ))}
      </div>
      )}

      {/* mobile: open the filters/paths sheet */}
      {isMobile && !tools && (
        <button className="exp-tools-fab" onClick={() => setTools(true)}><Icon d="layers" size={15} /> Sources & filters</button>
      )}

      {!isMobile && <div className="exp-hint">Numbers match the source list · select a source to read its citation</div>}
    </div>
  );
}

Object.assign(window, { ExplorePage });
